import React from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import {
  BodyText,
  Chip,
  SectionHeader,
  TextFieldCurrency,
  theme,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { calculateTip } from '../../../Checkout.utils';
import { CheckoutLayoutBlock } from '../../CheckoutLayout';

export const CheckoutTipBlock = (props: CheckoutTipBlockProps) => {
  const { t } = useLocalizationContext();
  const { subtotal, setTip, isDelivery } = props;
  const [showCustom, setShowCustom] = React.useState(false);

  if (!isDelivery) return null;

  return (
    <CheckoutLayoutBlock {...props}>
      <SectionHeader
        heading={t('general.delivery-tip')}
        style={styles.header}
      />

      {/* ========== ========== ========== ========== */}
      <View style={{ paddingTop: theme.spacing.large }}>
        <BodyText size={4}>{t('general.delivery-notice')}</BodyText>
      </View>

      {/* ========== ========== ========== ========== */}

      <View
        testID="checkout.tip.options"
        style={{ paddingVertical: theme.spacing.large }}
      >
        <CheckoutTipOptions
          subtotal={subtotal ?? 0}
          setShowCustom={setShowCustom}
          setTip={setTip}
        />
      </View>

      {/* ========== ========== ========== ========== */}

      {showCustom ? <CheckoutCustomTip setTip={setTip} /> : null}
    </CheckoutLayoutBlock>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type CheckoutTipBlockProps = {
  readonly subtotal: number;
  readonly setTip: (val: number) => void;
  readonly isDelivery: boolean;
} & Omit<ViewProps, 'children'>;

type CheckoutTipOptionsProps = {
  readonly subtotal: number;
  readonly setShowCustom: (text: boolean) => void;
  readonly setTip: (val: number) => void;
};

type CheckoutCustomTipProps = {
  readonly setTip: (text: number) => void;
};

// ─── STYLES ──────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  header: {
    paddingBottom: 0,
  },
});

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const CheckoutTipOptions = (props: CheckoutTipOptionsProps) => {
  const { t } = useLocalizationContext();
  const { subtotal, setShowCustom, setTip } = props;
  const { formatPrice } = useLocalizationContext();
  const tipValues = {
    '10': '10%',
    '15': '15%',
    '20': '20%',
  };

  const [selectedTip, setSelectedTip] = React.useState('15');
  const calculateLabel = (percentage: number) => {
    return formatPrice(calculateTip(subtotal, percentage), 'USD');
  };

  return (
    <Chip.Menu
      value={selectedTip}
      onChange={(val) => {
        setSelectedTip(val);
        const isCustom = val === 'custom';

        setShowCustom(isCustom);
        const tip = calculateTip(subtotal, Number.parseInt(val, 10));

        setTip(isCustom ? 0 : tip);
      }}
    >
      <Chip
        testID="checkout.tip.10"
        value="10"
        label={{ text: calculateLabel(10) }}
        accessibilityHint={calculateLabel(10)}
        accessibilityLabel={calculateLabel(10)}
      >
        {tipValues[10]}
      </Chip>
      <Chip
        testID="checkout.tip.15"
        value="15"
        label={{ text: calculateLabel(15) }}
        accessibilityHint={calculateLabel(15)}
        accessibilityLabel={calculateLabel(15)}
      >
        {tipValues[15]}
      </Chip>
      <Chip
        testID="checkout.tip.20"
        value="20"
        label={{ text: calculateLabel(20) }}
        accessibilityHint={calculateLabel(20)}
        accessibilityLabel={calculateLabel(20)}
      >
        {tipValues[20]}
      </Chip>
      <Chip
        testID="checkout.tip.custom"
        value="custom"
        label={{ spacing: true }}
        accessibilityHint={t('checkout.tip.custom')}
        accessibilityLabel={t('checkout.tip.custom')}
      >
        {t('checkout.tip.custom')}
      </Chip>
    </Chip.Menu>
  );
};

const CheckoutCustomTip = (props: CheckoutCustomTipProps) => {
  const { t } = useLocalizationContext();
  const { setTip } = props;

  return (
    <TextFieldCurrency
      accessibilityHint={t('checkout.tip.custom-field-a11y')}
      accessibilityLabel={t('checkout.tip.custom-field-a11y')}
      label={t('checkout.tip.custom-field')}
      onChangeText={setTip}
    />
  );
};
